import { delay, put, takeLatest } from "@redux-saga/core/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import { call } from "redux-saga/effects";
import fetchAPI, { ApiResponse, HttpMethod } from "../../../utils/api";
import { hasRole } from "../../../utils/roles";
import {
  showError,
  showSuccess,
  toasterSlice,
} from "../../toaster/store/slice";
import { RoleCode } from "../../users/store/slice";
import { LoginFormValues } from "../LoginPage";
import loginPageSlice, { Profile } from "./slice";

type LoginResponse = ApiResponse<{ token: string }>;

type ProfileResponse = ApiResponse<{ data: Profile }>;

function* loginPageSaga() {
  yield takeLatest(loginPageSlice.actions.login, handleLogin);
  yield takeLatest(loginPageSlice.actions.autoLogin, handleAutoLogin);
  yield takeLatest(loginPageSlice.actions.logout, handleLogout);
  yield takeLatest(loginPageSlice.actions.revokeToken, handleRevokeToken);
}

function* handleLogin({ payload }: PayloadAction<LoginFormValues>) {
  const response: LoginResponse = yield call(fetchAPI, "/tokens/create", {
    method: HttpMethod.POST,
    body: JSON.stringify(payload),
  });

  if (response && response.token) {
    localStorage.setItem("token", response.token);

    const profile: ProfileResponse = yield call(fetchProfile);

    if (profile) {
      yield put(toasterSlice.actions.hideAll());

      return;
    }
  }

  yield put(loginPageSlice.actions.setLoggedOut());
}

function* handleRevokeToken() {
  yield call(fetchAPI, "/tokens/revoke", {
    method: HttpMethod.POST,
  });
}

function* handleLogout(showNotification = true) {
  if (localStorage.getItem("token")) {
    localStorage.removeItem("token");
  }

  yield put(loginPageSlice.actions.setLoggedOut());

  if (showNotification) {
    yield put(showSuccess("Logged out."));
  }
}

function* handleAutoLogin() {
  yield delay(300);

  yield call(fetchProfile);
}

export function* fetchProfile() {
  const response: ProfileResponse = yield call(fetchAPI, "/profile");

  if (response && response.data) {
    const profile = response.data;
    const withoutRole = profile.roles.length === 0;
    const inactiveCompany =
      profile.companies[0] && !profile.companies[0].active;
    const customerWithoutCompany =
      hasRole(profile, RoleCode.Customer) && !profile.companies[0];
    const sapUser = hasRole(profile, RoleCode.Sap);

    const generalMessage = "Please contact manager to gain access.";

    if (withoutRole) {
      yield put(showError(`Your account is missing role. ${generalMessage}`));

      yield call(handleLogout, false);
    } else if (inactiveCompany) {
      yield put(
        showError(
          `You are not allowed to use this application. ${generalMessage}`
        )
      );

      yield call(handleLogout, false);
    } else if (customerWithoutCompany) {
      yield put(showError(`You have no companies assigned. ${generalMessage}`));

      yield call(handleLogout, false);
    } else if (sapUser) {
      yield put(showError(`You have no access to use interface.`));

      yield call(handleLogout, false);
    } else {
      yield put(loginPageSlice.actions.setLoggedIn(profile));

      return profile;
    }
  }
}

export default loginPageSaga;
